import React from 'react';

import { Box, Button } from '@mui/material';
import { matchPath, useLocation, useNavigate } from 'react-router';

import { appRoutes, RouterEntitiesId } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';
import { getDateTimeInTimezone } from '@clearverify/utils';

import {
  InboxItemType,
  InboxListItemInfoFragment,
  useUpdateInboxItemUnreadStatusMutation,
  VerificationRequestStatus,
  VerificationRequestStripePaymentStatus,
} from 'src/api';
import { DemoPrivacyAvatar } from 'src/components/demoPrivacyMode';
import { ModalId } from 'src/constants/modals';
import { useModal } from 'src/hooks/shared';
import { useCurrentUser, useCurrentUserPermissions } from 'src/hooks/user';
import { useVerificationRequestProcessing } from 'src/hooks/verificationRequest';
import {
  ApproveVerificationRequestByEmployeeModalArgs,
  DeclineVerificationRequestByEmployeeModalArgs,
} from 'src/modals/verificationRequest';
import { InboxItemExtended } from 'src/types/inbox';
import { buildUrl } from 'src/utils/router';

import { InboxItemStatusChip } from '../InboxItemStatusChip';
import { InboxItemStatusPayment } from '../InboxItemStatusPayment';

import { BaseInboxItemCard } from './BaseInboxItemCard';
import { InboxItemCardHeader } from './components';
import { consumerBtnStyles } from './styles';

interface InboxItemCardProps {
  inboxItem: InboxItemExtended<InboxListItemInfoFragment>;
}

export const InboxItemCard: React.FC<InboxItemCardProps> = ({ inboxItem }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { isVerifierResponding } = useVerificationRequestProcessing();

  const {
    isEmployee,
    isPlatformAdmin,
    isVerifierManager,
    isVerifierAdmin,
    hasEmployerRoles,
    hasPeoRoles,
    isPeoPayrollProviderUser,
  } = useCurrentUserPermissions();
  const { timezone } = useCurrentUser();

  const [updateInboxItemUnreadStatus] = useUpdateInboxItemUnreadStatusMutation();

  const isVerificationRequest = inboxItem.type === InboxItemType.VerificationRequest;
  const isApiVerificationRequest = inboxItem.type === InboxItemType.ApiVerificationRequest;

  const isVerifierResponseInProgress = isVerifierResponding(inboxItem.id);

  const inboxItemPath = React.useMemo(() => {
    const baseRoute =
      inboxItem.type === InboxItemType.TestVerificationRequest
        ? appRoutes.inbox.inboxTestRequestItem
        : appRoutes.inbox.inboxItem;
    return buildUrl(baseRoute, {
      [RouterEntitiesId.inboxItem]: inboxItem.id,
    });
  }, [inboxItem.type, inboxItem.id]);

  const isSelected = Boolean(matchPath({ path: inboxItemPath, end: true }, location.pathname));
  const isEmployeeVerificationRequest = isEmployee && isVerificationRequest;
  // Employees see who their data was shared with — surface the verifier just like a normal request.
  const isEmployeeApiVerificationRequest = isEmployee && isApiVerificationRequest;

  const cardHeading = React.useMemo(() => {
    const {
      lastActivityHeading,
      verifierCompanyName,
      verifierLogoUrl,
      employeeAvatarUrl,
      employeeFullName,
      contributorCompanyName,
      contributorLogoUrl,
    } = inboxItem;

    const cardHeading: {
      name: Nullable<string>;
      avatarUrl: Nullable<string>;
      lastActivity: Nullable<string>;
    } = {
      name: employeeFullName,
      avatarUrl: employeeAvatarUrl,
      lastActivity: !hasEmployerRoles ? lastActivityHeading : undefined,
    };
    if (isEmployeeVerificationRequest || isEmployeeApiVerificationRequest) {
      cardHeading.name = verifierCompanyName;
      cardHeading.avatarUrl = verifierLogoUrl;
    }
    if (hasPeoRoles) {
      cardHeading.name = contributorCompanyName;
      cardHeading.avatarUrl = contributorLogoUrl;
      cardHeading.lastActivity = `Employee name: ${employeeFullName}`;
    }
    if (isPeoPayrollProviderUser) {
      cardHeading.name = contributorCompanyName;
      cardHeading.avatarUrl = contributorLogoUrl;
      cardHeading.lastActivity = contributorCompanyName
        ? `Employer name: ${contributorCompanyName}\nEmployee name: ${employeeFullName}`
        : `Employee name: ${employeeFullName}`;
    }
    return cardHeading;
  }, [
    hasEmployerRoles,
    isPeoPayrollProviderUser,
    hasPeoRoles,
    inboxItem,
    isEmployeeVerificationRequest,
    isEmployeeApiVerificationRequest,
  ]);

  const isVerifyAdminOrVerifier = isPlatformAdmin || isVerifierManager || isVerifierAdmin;

  const navigateToDetails = React.useCallback(() => {
    navigate(inboxItemPath);
    if (inboxItem.isUnread) {
      updateInboxItemUnreadStatus({
        variables: {
          inboxItemId: inboxItem.id,
        },
      });
    }
  }, [inboxItem.id, inboxItem.isUnread, inboxItemPath, navigate, updateInboxItemUnreadStatus]);

  const { openModal } = useModal();

  const handleApprove = React.useCallback(() => {
    openModal(ModalId.ApproveVerificationRequestByEmployee, {
      inboxItemId: inboxItem.id,
    } satisfies ApproveVerificationRequestByEmployeeModalArgs);
  }, [inboxItem.id, openModal]);

  const handleDecline = React.useCallback(() => {
    openModal(ModalId.DeclineVerificationRequestByEmployee, {
      inboxItemId: inboxItem.id,
    } satisfies DeclineVerificationRequestByEmployeeModalArgs);
  }, [inboxItem.id, openModal]);

  const renderCardBottomContent = React.useCallback(() => {
    if (inboxItem.type === InboxItemType.TestVerificationRequest) {
      return null;
    }

    if (
      isEmployeeVerificationRequest &&
      inboxItem.subEntityStatus === VerificationRequestStatus.EmployeeToRespond &&
      !isVerifierResponseInProgress
    ) {
      return (
        <Box display="flex" mt={2} justifyContent="space-between" alignItems="center">
          <Box display="flex" justifyContent="center" ml={0.5}>
            <Button variant="text" color="error" onClick={handleDecline} sx={consumerBtnStyles}>
              Decline
            </Button>
            <Button variant="text" onClick={handleApprove} sx={consumerBtnStyles}>
              Approve
            </Button>
          </Box>
          {isVerifyAdminOrVerifier && (
            <InboxItemStatusPayment stripePaymentStatus={inboxItem.stripePaymentStatus} />
          )}
          <InboxItemStatusChip
            status={inboxItem.subEntityStatus}
            isVerificationRequest={isVerificationRequest}
            isApiVerificationRequest={isApiVerificationRequest}
          />
        </Box>
      );
    }

    return (
      <Box
        display="flex"
        mt={2}
        justifyContent={justifyContentStyles(
          isVerifyAdminOrVerifier,
          inboxItem.stripePaymentStatus,
        )}
      >
        {isVerifyAdminOrVerifier && (
          <InboxItemStatusPayment stripePaymentStatus={inboxItem.stripePaymentStatus} />
        )}
        <InboxItemStatusChip
          status={inboxItem.subEntityStatus}
          isVerificationRequest={isVerificationRequest}
          isApiVerificationRequest={isApiVerificationRequest}
        />
      </Box>
    );
  }, [
    inboxItem.type,
    inboxItem.subEntityStatus,
    inboxItem.stripePaymentStatus,
    isEmployeeVerificationRequest,
    isVerifierResponseInProgress,
    isVerifyAdminOrVerifier,
    isVerificationRequest,
    isApiVerificationRequest,
    handleDecline,
    handleApprove,
  ]);

  const lastActivityDateUserTimezone = getDateTimeInTimezone(
    inboxItem.lastActivityDate,
    timezone,
  )?.toISO();

  return (
    <BaseInboxItemCard
      avatar={<DemoPrivacyAvatar src={cardHeading.avatarUrl} variant="circular" />}
      isSelected={isSelected}
      onClick={navigateToDetails}
      title={
        <InboxItemCardHeader
          cardHeading={cardHeading.name}
          lastUpdatedAt={lastActivityDateUserTimezone}
          isUnreadStatus={inboxItem.isUnread}
        />
      }
      subheader={inboxItem.subEntityType}
      lastActivity={cardHeading.lastActivity}
    >
      {renderCardBottomContent()}
    </BaseInboxItemCard>
  );
};

const justifyContentStyles = (
  isVerifyAdminOrVerifier: boolean,
  stripePaymentStatus: Nullable<VerificationRequestStripePaymentStatus>,
) => {
  if (
    isVerifyAdminOrVerifier &&
    stripePaymentStatus &&
    (
      [
        VerificationRequestStripePaymentStatus.Canceled,
        VerificationRequestStripePaymentStatus.Processing,
      ] as string[]
    ).includes(stripePaymentStatus)
  ) {
    return 'space-between';
  }
  return 'flex-end';
};
